/**
 * /app/{slug}/support-tickets — Contact WorkRight: the company's tickets
 * to the WorkRight team (company admins and HR managers). Not the
 * company's own Helpdesk. /support-tickets/new is a child route that
 * replaces the list while open.
 *
 * Backend: GET /api/v1/support/tickets?filter=open|resolved|all.
 */
import { createFileRoute, Link, Outlet, useChildMatches } from "@tanstack/react-router";
import { Inbox, Plus } from "lucide-react";
import { z } from "zod";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { formatDate } from "@/lib/format";
import { toApiError } from "@/lib/api";
import { supportCategoryLabel } from "@/lib/support-tickets";
import type { SupportListFilter } from "@/lib/api/support-tickets";
import { useSupportTickets } from "@/hooks/use-support-tickets";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { SupportStatusPill } from "@/components/support/status-pill";
import { SupportOnlyAdmins } from "@/components/support/support-only-admins";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const searchSchema = z.object({ tab: z.enum(["open", "resolved", "all"]).optional() });

export const Route = createFileRoute("/_authgated/app/$tenantSlug/support-tickets")({
  validateSearch: searchSchema,
  component: SupportTicketsPage,
});

const TABS: { value: SupportListFilter; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" },
];

function SupportTicketsPage() {
  const { tenantSlug } = Route.useParams();
  const { tab = "open" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useAuth();
  const hasChild = useChildMatches({ select: (m) => m.length > 0 });
  const admin = isHRAdmin(user?.role);
  const ticketsQ = useSupportTickets(tab);
  if (hasChild) return <Outlet />;
  if (!admin) return <SupportOnlyAdmins />;

  const raise = (
    <Button asChild className="gap-1.5">
      <Link to="/app/$tenantSlug/support-tickets/new" params={{ tenantSlug }}>
        <Plus aria-hidden className="h-4 w-4" />
        Raise a Ticket
      </Link>
    </Button>
  );
  const tickets = ticketsQ.data ?? [];

  return (
    <>
      <PageHeader
        title="Support Tickets"
        description="Your company's questions to the WorkRight team. We reply within one working day."
        actions={raise}
      />
      <Tabs value={tab} onValueChange={(v) => void navigate({ search: { tab: v as SupportListFilter } })}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="pointer-coarse:min-h-11">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="mt-4">
        {ticketsQ.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        {ticketsQ.isError && (
          <Alert variant="destructive">
            <AlertDescription>{toApiError(ticketsQ.error).message}</AlertDescription>
          </Alert>
        )}
        {ticketsQ.data && tickets.length === 0 && (
          <EmptyState
            icon={Inbox}
            title={tab === "open" ? "Nothing open" : tab === "resolved" ? "No resolved tickets yet" : "No tickets yet"}
            description={
              tab === "open"
                ? "Questions about WorkRight, your plan or something that looks wrong? Raise a ticket and we'll reply within one working day."
                : undefined
            }
          />
        )}
        {tickets.length > 0 && (
          <ul className="divide-y rounded-lg border" data-testid="support-ticket-list">
            {tickets.map((t) => (
              <li key={t.id}>
                <Link
                  to="/app/$tenantSlug/support-tickets/$id"
                  params={{ tenantSlug, id: String(t.id) }}
                  className="flex flex-col gap-1 p-4 hover:bg-accent/50 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{t.subject}</span>
                    <span className="block text-xs text-muted-foreground">
                      <span className="font-mono">{t.number}</span> · {supportCategoryLabel(t.category)}
                      {t.raised_by_name ? ` · ${t.raised_by_name}` : ""}
                    </span>
                  </span>
                  <span className="flex items-center gap-3 text-xs text-muted-foreground">
                    <SupportStatusPill status={t.status} side="customer" />
                    <span>Updated {formatDate(t.last_activity_at)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
